import { Archivierungsstatus } from '@core/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { endOfDay } from 'date-fns';
import React from 'react';
import { toast } from 'sonner';

import {
  ALL_ARCHIVE_SEARCH_FIELDS,
  normalizeArchiveSearchFields,
} from '@/constants/archiveSearchFields';
import { useAuth } from '@/features/auth/AuthProvider';
import { VorgangProzessSchritt } from '@/features/schemaValidation/stepValidationSchemas';
import { getErrResponseDetail } from '@/lib/utils';
import type { VorgangFilters } from '@/types/filters';
import {
  AussteuerungsInformation,
  ProcessingStatus,
  VorgangListeItem,
} from '@/types/vorgang';
import {
  GetVorgaenge200ResponseDataInner,
  GetVorgang200Response,
  GetVorgangKommentare200ResponseInner,
  RetryEventRequestStartSchrittEnum,
} from './__generated';
import { getInvalidationKeys, queryKeys } from './queryKeys';
import { useApiClient } from './useApiClient';

// Backend liefert currentStep im /vorgaenge-Response
// das generierte OpenAPI-Schema kennt das Feld aber (noch) nicht.
type ApiVorgangsListeItem = GetVorgaenge200ResponseDataInner & {
  currentStep?: string | null;
};

const VORGANG_PROZESS_SCHRITT_VALUES = new Set<string>(
  Object.values(VorgangProzessSchritt),
);

function parseCurrentStep(
  raw: string | null | undefined,
): VorgangProzessSchritt | null {
  if (!raw) return null;
  return VORGANG_PROZESS_SCHRITT_VALUES.has(raw)
    ? (raw as VorgangProzessSchritt)
    : null;
}

export function buildVorgangListQueryParams(
  filters?: VorgangFilters,
  pagination?: { page: number; pageSize: number },
  sorting?: { sortBy: string; sortOrder: 'asc' | 'desc' },
  options?: { includeCurrentStepFilters?: boolean; archiveFilters?: boolean },
): Record<string, string | string[]> {
  const queryParams: Record<string, string | string[]> = {};

  if (filters) {
    const addColumnFilter = (
      field:
        | 'auftragsnummer'
        | 'vertragsnummer'
        | 'externerName'
        | 'dokumentTyp',
    ) => {
      if (!filters[field]) return;
      queryParams[field] = filters[field];
      const operatorField = `${field}Operator` as const;
      if (options?.archiveFilters && filters[operatorField] === 'equals')
        queryParams[operatorField] = 'equals';
    };

    if (filters.archivierungsstatus) {
      queryParams.archivierungsstatus = filters.archivierungsstatus;
    }
    if (filters.status) {
      queryParams.status = filters.status;
    }
    if (filters.createdAt.from) {
      queryParams.createdAtFrom = filters.createdAt.from.toISOString();
    }
    if (filters.createdAt.to) {
      const eod = endOfDay(new Date(filters.createdAt.to));
      queryParams.createdAtTo = eod.toISOString();
    }
    if (filters.currentGestartedAm.from) {
      queryParams.currentGestartedAmFrom =
        filters.currentGestartedAm.from.toISOString();
    }
    if (filters.currentGestartedAm.to) {
      const eod = endOfDay(new Date(filters.currentGestartedAm.to));
      queryParams.currentGestartedAmTo = eod.toISOString();
    }
    addColumnFilter('auftragsnummer');
    addColumnFilter('vertragsnummer');
    if (filters.kundennummer) {
      queryParams.kundennummer = filters.kundennummer;
    }
    if (filters.correlationId) {
      queryParams.correlationId = filters.correlationId;
    }
    addColumnFilter('externerName');
    if (filters.vorgangType) {
      queryParams.vorgangType = filters.vorgangType;
    }
    if (filters.externeId) {
      queryParams.externeId = filters.externeId;
    }
    if (filters.error) {
      queryParams.error = filters.error;
    }
    if (filters.lockedBy) {
      queryParams.lockedBy = filters.lockedBy;
    }
    if (filters.fehlertyp) {
      queryParams.fehlertyp = filters.fehlertyp;
    }
    addColumnFilter('dokumentTyp');
    if (filters.searchText) {
      queryParams.searchText = filters.searchText;
      const fields = normalizeArchiveSearchFields(filters.searchFields);
      if (
        options?.archiveFilters &&
        fields.length < ALL_ARCHIVE_SEARCH_FIELDS.length
      )
        queryParams.searchFields = fields.join(',');
      if (filters.searchOperator && filters.searchOperator !== 'contains') {
        queryParams.searchOperator = filters.searchOperator;
      }
    }
    if (filters.unterdrueckung) {
      queryParams.unterdrueckung = filters.unterdrueckung;
    }
    if (options?.includeCurrentStepFilters && filters.currentStep) {
      queryParams.currentStep = filters.currentStep;
    }
    if (options?.includeCurrentStepFilters && filters.stale24h) {
      queryParams.stale24h = 'true';
    }
  }

  if (pagination) {
    queryParams.offset = (
      (pagination.page - 1) *
      pagination.pageSize
    ).toString();
    queryParams.limit = pagination.pageSize.toString();
  }

  if (sorting) {
    queryParams.sortBy = sorting.sortBy;
    queryParams.sortOrder = sorting.sortOrder;
  }

  return queryParams;
}

export function flattenParamsForExportQuery(
  filters?: VorgangFilters,
  pagination?: { page: number; pageSize: number },
  sorting?: { sortBy: string; sortOrder: 'asc' | 'desc' },
  columns?: string[],
  options?: { includeCurrentStepFilters?: boolean; archiveFilters?: boolean },
): Record<string, string> {
  const raw = buildVorgangListQueryParams(
    filters,
    pagination,
    sorting,
    options,
  );
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw)) {
    out[k] = Array.isArray(v) ? v.join(',') : v;
  }
  if (columns?.length) {
    out.columns = columns.join(',');
  }
  return out;
}

// Query hooks
export const useVorgaenge = (
  filters?: VorgangFilters,
  pagination?: { page: number; pageSize: number },
  sorting?: { sortBy: string; sortOrder: 'asc' | 'desc' },
  options?: { enabled?: boolean },
) => {
  const { vorgaengeApi } = useApiClient();

  return useQuery({
    enabled: options?.enabled ?? true,
    queryKey: queryKeys.vorgaenge.list(filters, pagination, sorting),
    queryFn: async ({ signal }) => {
      const queryParams = buildVorgangListQueryParams(
        filters,
        pagination,
        sorting,
        { includeCurrentStepFilters: true },
      );

      const response = await vorgaengeApi.getVorgaenge(queryParams, { signal });
      return {
        data: response.data.data.map(transformApiVorgangsListeItem),
        count: response.data.count,
      };
    },
    staleTime: 30000, // Consider data fresh for 30 seconds
    gcTime: 300000, // Keep in cache for 5 minutes
  });
};

export const useVorgaengeArchiv = (
  filters?: VorgangFilters,
  pagination?: { page: number; pageSize: number },
  sorting?: { sortBy: string; sortOrder: 'asc' | 'desc' },
  options?: { enabled?: boolean },
) => {
  const { axiosInstance } = useApiClient();

  return useQuery({
    enabled: options?.enabled ?? true,
    queryKey: queryKeys.archiv.list(filters, pagination, sorting),
    queryFn: async ({ signal }) => {
      const queryParams = buildVorgangListQueryParams(
        filters,
        pagination,
        sorting,
        { archiveFilters: true },
      );
      const response = await axiosInstance.get<{
        data: GetVorgaenge200ResponseDataInner[];
        count: number;
      }>('/vorgaenge-archiv', { params: queryParams, signal });
      return {
        data: response.data.data.map(transformApiVorgangsListeItem),
        count: response.data.count,
      };
    },
    staleTime: 30000,
    gcTime: 300000,
  });
};

export type VorgangDetailResponse = GetVorgang200Response & {
  isArchived?: boolean;
};
export const useVorgang = (id: string) => {
  const { axiosInstance } = useApiClient();

  return useQuery<VorgangDetailResponse>({
    queryKey: queryKeys.vorgaenge.detail(id),
    queryFn: async () => {
      const response = await axiosInstance.get<VorgangDetailResponse>(
        `/vorgaenge/${id}`,
      );
      return response.data;
    },
    enabled: !!id, // Only run query if id is provided
    staleTime: 30000,
    gcTime: 300000,
  });
};

export const useVorgangKommentare = (
  id: string,
  options?: { enabled?: boolean },
) => {
  const { vorgaengeApi } = useApiClient();

  return useQuery<GetVorgangKommentare200ResponseInner[]>({
    queryKey: queryKeys.vorgaenge.kommentare(id),
    queryFn: async () => {
      const response = await vorgaengeApi.getVorgangKommentare({ id });
      return response.data;
    },
    staleTime: 30000,
    gcTime: 300000,
    enabled: options?.enabled ?? true,
  });
};

export const transformApiVorgangsListeItem = (
  item: GetVorgaenge200ResponseDataInner,
): VorgangListeItem => {
  const extended = item as ApiVorgangsListeItem;
  return {
    id: item.id,
    correlationId: item.correlationId,
    externeId: item.externeId,
    externerName: item.externerName,
    vorgangType: item.vorgangType,
    dokumentQuelldaten: item.dokumentQuelldaten,
    error: item.error,
    createdAt: new Date(item.createdAt),
    createdBy: item.createdBy,
    auftragsnummer: item.auftragsnummer,
    vertragsnummer: item.vertragsnummer,
    kundennummer: item.kundennummer,
    aussteuerungsInformation: transformApiAussteuerungsInformation(),
    lockedBy: item.lockedBy,
    deletedAt: item.deletedAt ? new Date(item.deletedAt) : undefined,
    deletedBy: item.deletedBy,
    lockedAt: item.lockedAt ? new Date(item.lockedAt) : undefined,
    lastModifiedBy: item.lastModifiedBy,
    lastModifiedAt: item.lastModifiedAt
      ? new Date(item.lastModifiedAt)
      : undefined,
    status: item.status as ProcessingStatus,
    currentStep: parseCurrentStep(extended.currentStep),
    currentGestartedAm: item.currentGestartedAm
      ? new Date(item.currentGestartedAm)
      : undefined,
    fehlertyp: item.fehlertyp || undefined,
    dokumentTyp: item.dokumentTyp || undefined,
    unterdrueckung: item.unterdrueckung || undefined,
    archivierungsstatus:
      item.archivierungsstatus ?? Archivierungsstatus.UNBEKANNT,
  };
};

/**
 * AussteuerungsInformation gibt es (noch?) nicht.
 */
export const transformApiAussteuerungsInformation =
  (): AussteuerungsInformation => {
    // if (!item) {
    return {
      valide: false,
      verworfen: false,
      ausgesteuert: false,
      validierungsNachrichten: [],
      unterdrueckungsNachrichten: [],
    };
    // }
    // return {
    //   valide: item.valide,
    //   verworfen: item.verworfen,
    //   ausgesteuert: item.ausgesteuert,
    //   unterdrueckungsNachrichten: item.unterdrueckungsNachrichten,
    //   validierungsNachrichten: item.validierungsNachrichten,
    // };
  };

// Mutation hooks
export const useRetryVorgang = () => {
  const { vorgaengeApi } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      id: string;
      eventData: string;
      errorType?: string;
      initiator: string;
      startSchritt?: string;
    }) => {
      const response = await vorgaengeApi.retryEvent({
        id: params.id,
        retryEventRequest: {
          eventData: params.eventData,
          errorType: params.errorType,
          initiator: params.initiator,
          startSchritt:
            params.startSchritt as RetryEventRequestStartSchrittEnum,
        },
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      toast.success('Prozess wurde erfolgreich neu gestartet.');
      // Invalidate relevant queries after successful retry
      const invalidationKeys = getInvalidationKeys(variables.id);
      invalidationKeys.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
    },
    onError: (error) => {
      toast.error('Fehler beim Neustarten des Prozesses.');

      // eslint-disable-next-line no-console
      console.error('Failed to retry event:', error);
    },
  });
};

export const useSaveVorgang = () => {
  const { vorgaengeApi } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      id: string;
      eventData: string;
      errorType?: string;
      initiator: string;
    }) => {
      const response = await vorgaengeApi.saveEvent({
        id: params.id,
        saveEventRequest: {
          eventData: params.eventData,
          errorType: params.errorType,
          initiator: params.initiator,
        },
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      toast.success('Änderungen wurden erfolgreich gespeichert.');
      // Invalidate relevant queries after successful save
      const invalidationKeys = getInvalidationKeys(variables.id);
      invalidationKeys.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
    },
    onError: (error) => {
      toast.error('Fehler beim Speichern der Änderungen.');

      // eslint-disable-next-line no-console
      console.error('Failed to save event:', error);
    },
  });
};

export const useZurueckstellenVorgang = () => {
  const { vorgaengeApi } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      id: string;
      initiator: string;
      kommentar: string;
    }) => {
      const response = await vorgaengeApi.stelleVorgangZurueck({
        id: params.id,
        user: params.initiator,
        stelleVorgangZurueckRequest: {
          kommentar: params.kommentar,
        },
      });

      return response.data;
    },
    onSuccess: (_, variables) => {
      toast.success('Vorgang wurde zurückgestellt.');
      const invalidationKeys = getInvalidationKeys(variables.id);
      invalidationKeys.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
    },
    onError: (error) => {
      toast.error('Fehler beim Zurückstellen des Vorgangs.');

      // eslint-disable-next-line no-console
      console.error('Failed to stelle vorgang zurück:', error);
    },
  });
};

export const useLockVorgang = () => {
  const { vorgaengeApi } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { id: string; user: string }) => {
      const response = await vorgaengeApi.lockEvent({
        id: params.id,
        lockEventRequest: {
          user: params.user,
        },
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Update the cache directly instead of invalidating to prevent refetch
      toast.success('Vorgang erfolgreich gesperrt');
      queryClient.setQueryData(
        queryKeys.vorgaenge.detail(variables.id),
        (oldData: VorgangDetailResponse | undefined) => {
          if (oldData) {
            return {
              ...oldData,
              vorgang: {
                ...oldData.vorgang,
                lockedBy: variables.user,
                lockedAt: new Date(),
              },
            };
          }

          return oldData;
        },
      );

      // Only invalidate list queries if needed
      queryClient.invalidateQueries({
        queryKey: queryKeys.vorgaenge.lists(),
        refetchType: 'none', // Don't trigger automatic refetch
      });
    },
    onError: (error) => {
      toast.error('Fehler beim Sperren des Vorgangs.');

      // eslint-disable-next-line no-console
      console.error('Failed to lock event:', error);
    },
  });
};

export const useUnlockVorgang = () => {
  const { vorgaengeApi } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { id: string }) => {
      const response = await vorgaengeApi.unlockEvent({
        id: params.id,
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Update the cache directly instead of invalidating to prevent refetch
      queryClient.setQueryData(
        queryKeys.vorgaenge.detail(variables.id),
        (oldData: VorgangDetailResponse | undefined) => {
          if (oldData) {
            return {
              ...oldData,
              vorgang: {
                ...oldData.vorgang,
                lockedBy: undefined,
                lockedAt: undefined,
              },
            };
          }
          return oldData;
        },
      );

      // Invalidate list queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.vorgaenge.lists(),
      });
    },
    onError: (error) => {
      toast.error('Fehler beim Entsperren des Vorgangs.');

      // eslint-disable-next-line no-console
      console.error('Failed to unlock event:', error);
    },
  });
};

export const useDeleteVorgang = () => {
  const { vorgaengeApi } = useApiClient();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (params: { id: string }) => {
      const response = await vorgaengeApi.deleteVorgang({
        id: params.id,
        user: user?.email,
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      toast.success('Vorgang erfolgreich gelöscht');
      queryClient.removeQueries({
        queryKey: queryKeys.vorgaenge.detail(variables.id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.vorgaenge.lists(),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.archiv.lists() });
    },
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('Failed to delete event:', error);

      // TODO: Sentry einbauen.
      // captureException(error);
      const errorDetail =
        error &&
        typeof error === 'object' &&
        'response' in error &&
        error.response &&
        typeof error.response === 'object' &&
        'data' in error.response &&
        error.response.data &&
        typeof error.response.data === 'object' &&
        'detail' in error.response.data
          ? getErrResponseDetail(error)
          : null;

      toast.error(
        errorDetail ||
          'Fehler beim Löschen des Vorgangs. Bitte informieren Sie das Flex-Team.',
      );
    },
  });
};

export const useRestoreVorgang = () => {
  const { vorgaengeApi } = useApiClient();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (params: { id: string }) => {
      const response = await vorgaengeApi.restoreVorgang({
        id: params.id,
        user: user?.email,
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      toast.success('Vorgang erfolgreich wiederhergestellt');
      // Invalidate relevant queries after successful restore
      const invalidationKeys = getInvalidationKeys(variables.id);
      invalidationKeys.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
    },
    onError: (error) => {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.error('Failed to restore event:', error);
      }
    },
  });
};

export const useBulkDeleteVorgaenge = () => {
  const { vorgaengeApi } = useApiClient();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (params: { ids: string[] }) => {
      if (!user?.email) {
        throw new Error('User email is required for bulk delete operation');
      }
      const response = await vorgaengeApi.bulkDeleteVorgaenge({
        bulkDeleteVorgaengeRequest: {
          ids: params.ids,
          user: user.email,
        },
      });
      return response.data;
    },
    onSuccess: (result) => {
      const successCount = result.success?.length || 0;
      const failedCount = result.failed?.length || 0;

      // Only show success toast if all operations succeeded (backup notification)
      if (failedCount === 0 && successCount > 0) {
        toast.success(
          `${successCount} ${successCount === 1 ? 'Vorgang' : 'Vorgänge'} erfolgreich gelöscht`,
        );
      }

      // Invalidate list queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.vorgaenge.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.archiv.lists(),
      });
    },
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('Failed to bulk delete vorgaenge:', error);
      toast.error(
        'Fehler beim Löschen der Vorgänge. Bitte informieren Sie das Flex-Team.',
      );
    },
  });
};

export const useBulkRetryVorgaenge = () => {
  const { vorgaengeApi } = useApiClient();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (params: { ids: string[] }) => {
      if (!user?.email) {
        throw new Error('User email is required for bulk retry operation');
      }
      const response = await vorgaengeApi.bulkRetryVorgaenge({
        bulkRetryVorgaengeRequest: {
          ids: params.ids,
          initiator: user.email,
        },
      });
      return response.data;
    },
    onSuccess: (result) => {
      const successCount = result.success?.length || 0;
      const failedCount = result.failed?.length || 0;

      // Only show success toast if all operations succeeded (backup notification)
      if (failedCount === 0 && successCount > 0) {
        toast.success(
          `${successCount} ${successCount === 1 ? 'Vorgang' : 'Vorgänge'} erfolgreich zur erneuten Verarbeitung gesendet`,
        );
      }

      // Invalidate list queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.vorgaenge.lists(),
      });
    },
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('Failed to bulk retry vorgaenge:', error);
      toast.error(
        'Fehler beim erneuten Verarbeiten der Vorgänge. Bitte informieren Sie das Flex-Team.',
      );
    },
  });
};

export const useBulkZurueckstellenVorgaenge = () => {
  const { vorgaengeApi } = useApiClient();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (params: { ids: string[]; kommentar: string }) => {
      if (!user?.email) {
        throw new Error(
          'User email is required for bulk zurückstellen operation',
        );
      }
      const response = await vorgaengeApi.bulkZurueckstellenVorgaenge({
        bulkZurueckstellenVorgaengeRequest: {
          ids: params.ids,
          initiator: user.email,
          kommentar: params.kommentar,
        },
      });
      return response.data;
    },
    onSuccess: (result) => {
      const successCount = result.success?.length || 0;
      const failedCount = result.failed?.length || 0;

      if (failedCount === 0 && successCount > 0) {
        toast.success(
          `${successCount} ${successCount === 1 ? 'Vorgang' : 'Vorgänge'} erfolgreich zurückgestellt`,
        );
      }

      queryClient.invalidateQueries({
        queryKey: queryKeys.vorgaenge.lists(),
      });
    },
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('Failed to bulk zurückstellen vorgaenge:', error);
      toast.error(
        'Fehler beim Zurückstellen der Vorgänge. Bitte informieren Sie das Flex-Team.',
      );
    },
  });
};

const useExportVorgaengeCsvForSource = (
  source: 'main' | 'archiv',
  filename: string,
) => {
  const { axiosInstance } = useApiClient();
  const [isExporting, setIsExporting] = React.useState(false);

  const exportCsv = async (params: {
    filters?: VorgangFilters;
    pagination?: { page: number; pageSize: number };
    columns?: string[];
    limit?: string;
    sorting?: { sortBy: string; sortOrder: 'asc' | 'desc' };
  }) => {
    setIsExporting(true);
    try {
      const queryParams = flattenParamsForExportQuery(
        params.filters,
        params.pagination,
        params.sorting,
        params.columns,
        {
          includeCurrentStepFilters: source === 'main',
          archiveFilters: source === 'archiv',
        },
      );
      if (params.limit) {
        queryParams.limit = params.limit;
      }

      const path =
        source === 'archiv' ? '/vorgaenge-archiv/export' : '/vorgaenge/export';

      const response = await axiosInstance.get<{ downloadUrl: string }>(path, {
        params: queryParams,
      });
      const { downloadUrl } = response.data;

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to export vorgaenge:', error);
      toast.error('Fehler beim Export der Vorgänge.');
      throw error;
    } finally {
      setIsExporting(false);
    }
  };

  return { exportCsv, isExporting };
};

export const useExportMainVorgaengeCsv = () =>
  useExportVorgaengeCsvForSource('main', 'vorgaenge.csv');

export const useExportArchivVorgaengeCsv = () =>
  useExportVorgaengeCsvForSource('archiv', 'vorgaenge-archiv.csv');
